/**
 * Display Main Page - Customer-facing order status display
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { pwaPath } from '@/utils/getBasePath';
import { 
  Clock, CheckCircle2, ChefHat, Volume2, VolumeX, 
  Settings, Maximize2, RefreshCw 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDisplay } from '../context/DisplayContext';
import { useAccount } from '@/lib/pwaAccount';

export default function DisplayMainPage() {
  const { orders, stats, promotions, soundEnabled, loading, error, setSoundEnabled, refresh } = useDisplay();
  const account = useAccount();
  const restaurantName = account?.restaurant?.name || 'Order Status';
  const [currentTime, setCurrentTime] = useState(new Date());
  const [promoIndex, setPromoIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Update clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Rotate promotions
  useEffect(() => {
    if (promotions.length > 1) {
      const timer = setInterval(() => {
        setPromoIndex(prev => (prev + 1) % promotions.length);
      }, 8000);
      return () => clearInterval(timer);
    }
  }, [promotions.length]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Clock className="h-20 w-20 text-blue-500 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-2xl">Loading Display...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <span className="text-2xl font-bold">{restaurantName.charAt(0).toUpperCase()}</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold">{restaurantName}</h1>
            <p className="text-sm text-slate-400">Order Status</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-4xl font-mono">
            {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={soundEnabled ? 'text-green-400' : 'text-slate-500'}
            >
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={refresh}>
              <RefreshCw className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleFullscreen}>
              <Maximize2 className="h-5 w-5" />
            </Button>
            <Link to={pwaPath('display', 'settings')}>
              <Button variant="ghost" size="icon">
                <Settings className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {error && (
        <div className="mx-6 mb-2 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200" role="alert">
          {error}
        </div>
      )}

      {/* Main Content */}
      <main className="px-6 pb-6">
        <div className="grid grid-cols-2 gap-8">
          {/* Preparing Column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 bg-yellow-500 rounded-full animate-pulse" />
              <h2 className="text-3xl font-bold">Preparing</h2>
              <span className="text-2xl text-slate-400">({orders.preparing.length})</span>
            </div>
            
            <div className="space-y-4">
              {orders.preparing.map((order) => (
                <div 
                  key={order.id}
                  className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <ChefHat className="w-8 h-8 text-yellow-500" />
                      <div>
                        <p className="text-4xl font-bold">#{order.order_number}</p>
                        <p className="text-xl text-slate-400">{order.customer_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Estimated</p>
                      <p className="text-2xl font-mono text-yellow-400">~5 min</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {orders.preparing.length === 0 && (
                <div className="text-center py-12 bg-slate-800/30 rounded-2xl">
                  <ChefHat className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <p className="text-xl text-slate-500">No orders preparing</p>
                </div>
              )}
            </div>
          </div>

          {/* Ready Column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse" />
              <h2 className="text-3xl font-bold">Ready for Pickup</h2>
              <span className="text-2xl text-slate-400">({orders.ready.length})</span>
            </div>
            
            <div className="space-y-4">
              {orders.ready.map((order, index) => (
                <div 
                  key={order.id}
                  className={`bg-green-500/20 backdrop-blur rounded-2xl p-6 border-2 border-green-500 ${
                    index === 0 ? 'animate-pulse' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <CheckCircle2 className="w-10 h-10 text-green-500" />
                      <div>
                        <p className="text-5xl font-bold text-green-400">#{order.order_number}</p>
                        <p className="text-2xl text-green-300">{order.customer_name}</p>
                      </div>
                    </div>
                    <div className="text-green-400 text-xl font-bold">
                      READY!
                    </div>
                  </div>
                </div>
              ))}
              
              {orders.ready.length === 0 && (
                <div className="text-center py-12 bg-slate-800/30 rounded-2xl">
                  <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <p className="text-xl text-slate-500">No orders ready</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Promo Banner */}
      {promotions.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-orange-600 to-red-600 p-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-3xl font-bold">{promotions[promoIndex]?.title}</p>
            <p className="text-xl text-white/80 mt-2">{promotions[promoIndex]?.description}</p>
          </div>
        </div>
      )}

      {/* Stats Footer */}
      <div className="fixed bottom-24 left-6 right-6 flex justify-center gap-12">
        <div className="text-center">
          <p className="text-4xl font-bold text-blue-400">{stats ? (stats.avg_wait_time ?? 0) : '—'}</p>
          <p className="text-slate-400">Avg. Wait (min)</p>
        </div>
        <div className="text-center">
          <p className="text-4xl font-bold text-slate-300">{stats ? (stats.total_today ?? 0) : '—'}</p>
          <p className="text-slate-400">Orders Today</p>
        </div>
      </div>
    </div>
  );
}
